import React, { useState } from "react";
import axios from "axios";
import {
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from "@mui/material";
import API_BASE_URL from "../../utils/api";

const CATEGORY_OPTIONS = [
  { value: "George Washington Papers", label: "George Washington Papers" },
  { value: "War and Military", label: "War and Military" },
  { value: "Civil Rights", label: "Civil Rights" },
  { value: "Civil War Letters", label: "Civil War Letters" },
  { value: "Children's Literature", label: "Children's Literature" },
  { value: "Poetry", label: "Poetry" },
  { value: "Freedom Summer diaries", label: "Freedom Summer diaries" },
  { value: "Cookbook", label: "Cookbook" },
  {
    value: "Mississippi Armed Forces Museum",
    label: "Mississippi Armed Forces Museum",
  },

  { value: "Other", label: "Other" },
];

const SUBCATEGORY_OPTIONS = {
  "War and Military": [
    { value: "Vietnam", label: "Vietnam" },
    { value: "World War II", label: "World War II" },
    { value: "Civil War", label: "Civil War" },
  ],
};

const FileUpload = () => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(selected);
  };

  const areAllSelectedFilesTiff =
    files.length > 0 &&
    files.every((file) => {
      const name = (file.name || "").toLowerCase();
      return name.endsWith(".tif") || name.endsWith(".tiff");
    });

  const hasAnySelectedFiles = files.length > 0;

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setCategory("");
    setSubcategory("");
    setFiles([]);
  };

  const buildFormData = () => {
    const formData = new FormData();
    formData.append("title", title);
    formData.append("description", description);
    formData.append("category", category);
    formData.append("subcategory", subcategory);
    files.forEach((f) => formData.append("files", f));
    return formData;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("No token found — please log in.");
      return;
    }

    if (!files.length) {
      setMessage("Please select at least one file.");
      return;
    }

    try {
      setIsUploading(true);
      setMessage("Uploading files...");

      const formData = buildFormData();

      const res = await axios.post(
        `${API_BASE_URL}/documents/upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      console.log("upload response", res.data);
      setMessage("Files uploaded successfully!");
      resetForm();
    } catch (err) {
      console.error("upload error", err.response ? err.response.data : err);
      setMessage(
        err.response?.data?.error || "File upload failed — please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleBulkTiffSubmit = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setMessage("No token found — please log in.");
      return;
    }

    if (!files.length) {
      setMessage("Please select at least one TIFF file.");
      return;
    }

    if (!areAllSelectedFilesTiff) {
      setMessage("Bulk TIFF upload only accepts .tif or .tiff files.");
      return;
    }

    try {
      setIsUploading(true);
      setMessage("Uploading bulk TIFF set...");

      const formData = buildFormData();

      const res = await axios.post(
        `${API_BASE_URL}/documents/upload-bulk-tiff`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      console.log("bulk TIFF upload response", res.data);

      setMessage(
        `Bulk TIFF upload completed. ${res.data.totalPages || 0} page(s) created from ${
          res.data.processedFiles || 0
        } file(s).`
      );
      resetForm();
    } catch (err) {
      console.error(
        "bulk TIFF upload error",
        err.response ? err.response.data : err
      );
      setMessage(
        err.response?.data?.error ||
          "Bulk TIFF upload failed — please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        padding: "20px",
        borderRadius: "8px",
        backgroundColor: "#fff",
        maxWidth: "400px",
        margin: "auto",
      }}
    >
      <Typography
        variant="h5"
        sx={{
          marginBottom: "10px",
          textAlign: "center",
          fontWeight: "bold",
          color: "#333",
        }}
      >
        Upload a Document
      </Typography>

      <form onSubmit={handleSubmit}>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
          sx={{ marginBottom: "10px" }}
        />

        <TextField
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          required
          sx={{ marginBottom: "10px" }}
        />

        <FormControl fullWidth sx={{ marginBottom: "10px" }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={category}
            label="Category"
            onChange={(e) => {
              const next = e.target.value;
              setCategory(next);
              setSubcategory("");
            }}
            required
          >
            {CATEGORY_OPTIONS.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {SUBCATEGORY_OPTIONS[category] && (
          <FormControl fullWidth sx={{ marginBottom: "10px" }}>
            <InputLabel>Subcategory</InputLabel>
            <Select
              value={subcategory}
              label="Subcategory"
              onChange={(e) => setSubcategory(e.target.value)}
              required
            >
              {SUBCATEGORY_OPTIONS[category].map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button
          variant="contained"
          component="label"
          sx={{ display: "block", marginBottom: "10px" }}
          disabled={isUploading}
        >
          Choose Files
          <input
            type="file"
            hidden
            multiple
            accept="application/pdf,image/*,.tif,.tiff,.rtf,.zip"
            onChange={handleFileChange}
            required
          />
        </Button>

        {files.length > 0 && (
          <Typography sx={{ fontSize: "14px", color: "gray" }}>
            {files.length} file(s) selected
          </Typography>
        )}

        {hasAnySelectedFiles && !areAllSelectedFilesTiff && (
          <Typography
            sx={{ fontSize: "13px", color: "#8a6d3b", marginTop: "6px" }}
          >
            Bulk TIFF upload appears only when all selected files are .tif or
            .tiff.
          </Typography>
        )}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          sx={{ marginTop: "10px" }}
          disabled={isUploading}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>

        {areAllSelectedFilesTiff && (
          <Button
            type="button"
            variant="outlined"
            fullWidth
            sx={{ marginTop: "10px" }}
            onClick={handleBulkTiffSubmit}
            disabled={isUploading}
          >
            {isUploading ? "Uploading..." : "Upload Bulk TIFF Set"}
          </Button>
        )}
      </form>

      {message && (
        <Typography
          sx={{
            marginTop: "10px",
            textAlign: "center",
            color:
              message.toLowerCase().includes("failed") ||
              message.toLowerCase().includes("error") ||
              message.toLowerCase().includes("no token")
                ? "error.main"
                : "green",
          }}
        >
          {message}
        </Typography>
      )}
    </Paper>
  );
};

export default FileUpload;
